import { getCalendarWeek } from "@/lib/daycare-hotel/stayPlans";

export interface WeeklyTargetAdjustment {
  delta: number;
  targetWeekStartsOn: string | null;
}

export interface WeeklyRemovalUsage {
  usedOn: string;
  units: number;
}

function addWeeks(weekStartsOn: string, weeks: number) {
  const date = new Date(`${weekStartsOn}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + weeks * 7);
  return date.toISOString().slice(0, 10);
}

/** A cota efetiva de uma semana só considera ajustes apontados para ela. */
export function getEffectiveWeeklyLimit({
  usageLimit,
  weekStartsOn,
  adjustments,
}: {
  usageLimit: number;
  weekStartsOn: string;
  adjustments: WeeklyTargetAdjustment[];
}) {
  const adjustmentTotal = adjustments
    .filter((adjustment) => adjustment.targetWeekStartsOn === weekStartsOn)
    .reduce((total, adjustment) => total + adjustment.delta, 0);

  return Math.max(0, usageLimit + adjustmentTotal);
}

/**
 * A primeira semana que ainda absorve uma remoção. O limite é estendido até
 * uma semana após a última ocupação/ajuste conhecido, por isso atravessa ciclos
 * sem precisar de uma duração fixa de contrato.
 */
export function resolveWeeklyRemovalTarget({
  reference,
  usageLimit,
  usages,
  adjustments,
}: {
  reference: string;
  usageLimit: number;
  usages: WeeklyRemovalUsage[];
  adjustments: WeeklyTargetAdjustment[];
}): { weekStartsOn: string } {
  const currentWeek = getCalendarWeek(reference).start;
  const relevantWeeks = [
    ...usages.map((usage) => getCalendarWeek(usage.usedOn).start),
    ...adjustments.flatMap((adjustment) => adjustment.targetWeekStartsOn ? [adjustment.targetWeekStartsOn] : []),
  ].filter((week) => week >= currentWeek);
  const lastKnownWeek = relevantWeeks.sort().at(-1) ?? currentWeek;
  const numberOfWeeks = Math.max(
    1,
    Math.round((new Date(`${lastKnownWeek}T00:00:00.000Z`).getTime() - new Date(`${currentWeek}T00:00:00.000Z`).getTime()) / 604_800_000) + 2,
  );

  for (let offset = 0; offset < numberOfWeeks; offset += 1) {
    const weekStartsOn = addWeeks(currentWeek, offset);
    const used = usages
      .filter((usage) => getCalendarWeek(usage.usedOn).start === weekStartsOn)
      .reduce((total, usage) => total + usage.units, 0);
    const effectiveLimit = getEffectiveWeeklyLimit({ usageLimit, weekStartsOn, adjustments });

    if (used < effectiveLimit) return { weekStartsOn };
  }

  return { weekStartsOn: addWeeks(lastKnownWeek, 1) };
}
